import type { BreakdownRow, RevenueBasis, RevenueBreakdown, RevenueSummary } from '@/api/revenue'
import type { Envelope } from '@/api/types'
import { Card, formatInteger, formatPercent, type FormatOptions } from '@/design'
import { CardQueryBody } from '@/features/overview/CardQueryBody'
import type { GlobalFilters } from '@/lib/filters'
import { useFilterSearch } from '@/lib/useGlobalFilters'

import { BASIS_NOUN, CHANNEL_LABELS, NULL_REASON_TEXT } from '../format'
import { PanelNote, ShareBars, type ShareBarRow } from '../pieces'
import { useRevenueBreakdown } from '../queries'

/** The page's summary read, which the KPI row and this panel share. */
export interface SummaryRead {
  isPending: boolean
  isError: boolean
  isFetching: boolean
  data: Envelope<RevenueSummary> | undefined
}

/** How many competitions the Summary ranks before the rest fold into one row. */
export const TOP_COMPETITIONS = 6
const TOP_PAYMENT_METHODS = 6

/** The API's own row names for the fixed rows the Summary relabels for its context. */
const OTHER_LABELS: Partial<Record<RevenueBreakdown['by'], string>> = {
  competition: 'Other competitions',
  payment_method: 'Other methods',
}

/** A breakdown's rows as share bars, in the API's order (values best first, then `other`, then the fixed rows). */
export function shareRows(breakdown: RevenueBreakdown, href?: (row: BreakdownRow) => string | undefined): ShareBarRow[] {
  return breakdown.rows.map((row) => ({
    key: row.key,
    label: row.kind === 'other' ? (OTHER_LABELS[breakdown.by] ?? row.label) : row.label,
    value: row.revenue,
    share: row.share,
    href: row.kind === 'value' ? href?.(row) : undefined,
    fixed: row.kind !== 'value',
  }))
}

function ShareNote({ breakdown }: { breakdown: RevenueBreakdown }) {
  return breakdown.shareNullReason ? <PanelNote>Shares not available · {NULL_REASON_TEXT[breakdown.shareNullReason]}</PanelNote> : null
}

/** The top competitions by revenue under the basis, each a link to its own page with the filters carried. */
export function CompetitionPanel({ filters, basis, money }: { filters: GlobalFilters; basis: RevenueBasis; money: FormatOptions }) {
  const read = useRevenueBreakdown(filters, basis, 'competition', { limit: TOP_COMPETITIONS })
  const search = useFilterSearch()
  return (
    <Card className="rv-panel rv-panel--side" title="By competition" subtitle={`Top ${TOP_COMPETITIONS} · share of ${BASIS_NOUN[basis]}`} busy={read.isFetching}>
      <CardQueryBody query={read} loadingRows={6} errorDescription="Revenue by competition could not be loaded.">
        {({ data }) => (
          <>
            <ShareBars
              caption={`Revenue by competition, top ${TOP_COMPETITIONS}`}
              rows={shareRows(data, (row) => `/analytics/competitions/${encodeURIComponent(row.key)}${search}`)}
              money={money}
              empty="No competition revenue in this period."
            />
            <ShareNote breakdown={data} />
          </>
        )}
      </CardQueryBody>
    </Card>
  )
}

export function PaymentMethodPanel({
  filters,
  basis,
  money,
  siteCredit,
}: {
  filters: GlobalFilters
  basis: RevenueBasis
  money: FormatOptions
  /** The summary says site credit is in every figure, unsplit from cash (decision 2). */
  siteCredit: boolean
}) {
  const read = useRevenueBreakdown(filters, basis, 'payment_method', { limit: TOP_PAYMENT_METHODS })
  return (
    <Card className="rv-panel" title="By payment method" subtitle="Provider · method, as recorded" busy={read.isFetching}>
      <CardQueryBody query={read} loadingRows={5} errorDescription="Revenue by payment method could not be loaded.">
        {({ data }) => (
          <>
            <ShareBars caption="Revenue by payment method" rows={shareRows(data)} money={money} empty="No revenue in this period." />
            <ShareNote breakdown={data} />
          </>
        )}
      </CardQueryBody>
      {siteCredit ? <PanelNote>Site credit included, unsplit: credit spent is in each method’s revenue until the connector reports the split.</PanelNote> : null}
    </Card>
  )
}

function concentrationRow(key: string, label: string, value: number | null, reason: keyof typeof NULL_REASON_TEXT | null, detail?: string): ShareBarRow {
  return {
    key,
    label,
    value,
    share: null,
    nullLabel: reason ? `Not available · ${NULL_REASON_TEXT[reason]}` : '—',
    detail,
  }
}

/**
 * How concentrated the period's revenue is, the summary's three shares as it gives them:
 * the top tenth of identified customers, the top five competitions and the top channel
 * (last non-direct). Nothing here is worked out in the browser: the top tenth needs every
 * customer.
 */
export function ConcentrationPanel({ summary, money }: { summary: SummaryRead; money: FormatOptions }) {
  return (
    <Card className="rv-panel" title="Concentration" subtitle="Share of the period’s revenue" busy={summary.isFetching}>
      <CardQueryBody query={summary} loadingRows={3} errorDescription="Concentration could not be loaded.">
        {({ data }) => <ConcentrationBars summary={data} money={money} />}
      </CardQueryBody>
    </Card>
  )
}

function ConcentrationBars({ summary, money }: { summary: RevenueSummary; money: FormatOptions }) {
  const { topDecileCustomers: decile, topFiveCompetitions: five, topChannel: channel } = summary.concentration
  const customers = `${formatInteger(decile.customers, money)} identified ${decile.customers === 1 ? 'customer' : 'customers'}`
  const guests = decile.guestShare === null ? '' : ` · guests ${formatPercent(decile.guestShare)} of revenue, left out`
  const channelName = channel.channel ? (CHANNEL_LABELS[channel.channel] ?? channel.channel) : null
  return (
    <ShareBars
      caption="Revenue concentration"
      format="percent"
      money={money}
      rows={[
        concentrationRow('top_decile_customers', 'Top 10% of customers', decile.value, decile.nullReason, `${customers}${guests}`),
        concentrationRow('top_5_competitions', 'Top 5 competitions', five.value, five.nullReason),
        concentrationRow('top_channel', channelName ? `Top channel: ${channelName}` : 'Top channel', channel.value, channel.nullReason, 'last non-direct'),
      ]}
    />
  )
}
